import type { LearningItemView } from '../../api/content.types';
import type { KanaGroupKey } from './types';

export interface KanaGroup {
  key: KanaGroupKey;
  /** Nhãn tab + tiêu đề màn flashcard. */
  title: string;
  /** Dòng phụ dưới tab bar. */
  subtitle: string;
  cards: LearningItemView[];
}

/// Tuple [ký tự, romaji, nghĩa] — nghĩa rỗng với kana, âm Hán-Việt + nghĩa với bộ thủ.
type CardTuple = [string, string, string];

const HIRAGANA_CARDS: CardTuple[] = [
  ['あ', 'a', ''], ['い', 'i', ''], ['う', 'u', ''], ['え', 'e', ''], ['お', 'o', ''],
  ['か', 'ka', ''], ['き', 'ki', ''], ['く', 'ku', ''], ['け', 'ke', ''], ['こ', 'ko', ''],
  ['さ', 'sa', ''], ['し', 'shi', ''], ['す', 'su', ''], ['せ', 'se', ''], ['そ', 'so', ''],
  ['た', 'ta', ''], ['ち', 'chi', ''], ['つ', 'tsu', ''], ['て', 'te', ''], ['と', 'to', ''],
  ['な', 'na', ''], ['に', 'ni', ''], ['ぬ', 'nu', ''], ['ね', 'ne', ''], ['の', 'no', ''],
  ['は', 'ha', ''], ['ひ', 'hi', ''], ['ふ', 'fu', ''], ['へ', 'he', ''], ['ほ', 'ho', ''],
  ['ま', 'ma', ''], ['み', 'mi', ''], ['む', 'mu', ''], ['め', 'me', ''], ['も', 'mo', ''],
  ['や', 'ya', ''], ['ゆ', 'yu', ''], ['よ', 'yo', ''],
  ['ら', 'ra', ''], ['り', 'ri', ''], ['る', 'ru', ''], ['れ', 're', ''], ['ろ', 'ro', ''],
  ['わ', 'wa', ''], ['を', 'wo', ''], ['ん', 'n', ''],
];

const KATAKANA_CARDS: CardTuple[] = [
  ['ア', 'a', ''], ['イ', 'i', ''], ['ウ', 'u', ''], ['エ', 'e', ''], ['オ', 'o', ''],
  ['カ', 'ka', ''], ['キ', 'ki', ''], ['ク', 'ku', ''], ['ケ', 'ke', ''], ['コ', 'ko', ''],
  ['サ', 'sa', ''], ['シ', 'shi', ''], ['ス', 'su', ''], ['セ', 'se', ''], ['ソ', 'so', ''],
  ['タ', 'ta', ''], ['チ', 'chi', ''], ['ツ', 'tsu', ''], ['テ', 'te', ''], ['ト', 'to', ''],
  ['ナ', 'na', ''], ['ニ', 'ni', ''], ['ヌ', 'nu', ''], ['ネ', 'ne', ''], ['ノ', 'no', ''],
  ['ハ', 'ha', ''], ['ヒ', 'hi', ''], ['フ', 'fu', ''], ['ヘ', 'he', ''], ['ホ', 'ho', ''],
  ['マ', 'ma', ''], ['ミ', 'mi', ''], ['ム', 'mu', ''], ['メ', 'me', ''], ['モ', 'mo', ''],
  ['ヤ', 'ya', ''], ['ユ', 'yu', ''], ['ヨ', 'yo', ''],
  ['ラ', 'ra', ''], ['リ', 'ri', ''], ['ル', 'ru', ''], ['レ', 're', ''], ['ロ', 'ro', ''],
  ['ワ', 'wa', ''], ['ヲ', 'wo', ''], ['ン', 'n', ''],
];

const RADICAL_CARDS: CardTuple[] = [
  ['人', 'nhân', 'người'], ['水', 'thủy', 'nước'], ['火', 'hoả', 'lửa'], ['木', 'mộc', 'cây'],
  ['金', 'kim', 'vàng, kim loại'], ['土', 'thổ', 'đất'], ['日', 'nhật', 'ngày, mặt trời'],
  ['月', 'nguyệt', 'tháng, mặt trăng'], ['山', 'sơn', 'núi'], ['川', 'xuyên', 'sông'],
  ['口', 'khẩu', 'miệng'], ['手', 'thủ', 'tay'], ['目', 'mục', 'mắt'], ['心', 'tâm', 'tim, tâm trí'],
  ['女', 'nữ', 'phụ nữ'], ['子', 'tử', 'con'], ['糸', 'mịch', 'sợi tơ'], ['竹', 'trúc', 'tre'],
  ['虫', 'trùng', 'sâu bọ'], ['貝', 'bối', 'vỏ sò'],
];

/**
 * Dựng 1 thẻ `LearningItemView` (nhánh KANA/RADICAL) từ tuple nguồn.
 * `ttsText: null` là CỐ Ý: `ttsTextOf` tự fallback về `character`.
 * `meaningVi` phải là `null` (không phải `''`) khi tuple rỗng, vì `FlashcardBack`
 * kiểm bằng `{item.meaningVi && ...}` và chuỗi rỗng sẽ render phần tử trống thừa.
 */
function toItem(key: KanaGroupKey, index: number, tuple: CardTuple, kanaType: 'HIRAGANA' | 'KATAKANA' | null): LearningItemView {
  const [character, romaji, meaning] = tuple;
  return {
    id: `${key}-${index}`,
    itemType: kanaType ? 'KANA' : 'RADICAL',
    order: index,
    character,
    meaningVi: meaning === '' ? null : meaning,
    strokeCount: null,
    romaji,
    kanaType,
    onYomi: null,
    kunYomi: null,
    ttsText: null,
  };
}

export const KANA_GROUPS: KanaGroup[] = [
  { key: 'hiragana', title: 'Hiragana', subtitle: 'Bảng chữ mềm · 46 ký tự', cards: HIRAGANA_CARDS.map((t, i) => toItem('hiragana', i, t, 'HIRAGANA')) },
  { key: 'katakana', title: 'Katakana', subtitle: 'Bảng chữ cứng · 46 ký tự', cards: KATAKANA_CARDS.map((t, i) => toItem('katakana', i, t, 'KATAKANA')) },
  { key: 'radicals', title: 'Bộ thủ', subtitle: '20 bộ thủ phổ biến', cards: RADICAL_CARDS.map((t, i) => toItem('radicals', i, t, null)) },
];

export function findKanaGroup(key: string): KanaGroup | undefined {
  return KANA_GROUPS.find((g) => g.key === key);
}
